"use client";

import { getImageModelSettings } from "@saas/ai-image-generator/config/model-settings";
import type { ImageResolution } from "@saas/ai-image-generator/types";
import { cn } from "@ui/lib";
import { ChevronDownIcon, LockIcon } from "lucide-react";
import { AspectRatioSwatch } from "./AspectRatioSwatch";
import { GenerationSettings } from "./GenerationSettings";

export function ImageEffectSettings({
	aspectRatio,
	setAspectRatio,
	resolution,
	setResolution,
	lockedResolutions,
	onLockedResolution,
}: {
	aspectRatio: string;
	setAspectRatio: (value: string) => void;
	resolution: ImageResolution;
	setResolution: (value: ImageResolution) => void;
	lockedResolutions: Set<string>;
	onLockedResolution: () => void;
}) {
	const settings = getImageModelSettings("nano-banana-2-1");
	const ratio = ["original", "match_input_image"].includes(aspectRatio)
		? "auto"
		: aspectRatio;
	const optionClass = (selected: boolean) =>
		cn(
			"flex h-9 items-center justify-center gap-1.5 rounded-xl text-xs font-medium transition-colors",
			selected
				? "bg-zinc-950 text-white shadow-sm"
				: "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 hover:text-zinc-900",
		);
	return (
		<GenerationSettings
			trigger={
				<button
					type="button"
					aria-label={`Aspect Ratio ${ratio}, Resolution ${resolution}`}
					className="group flex h-12 w-full min-w-0 shrink-0 items-center rounded-xl border border-black/5 bg-zinc-100 px-3 text-sm font-semibold text-zinc-700 transition-colors hover:bg-zinc-200/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 data-[state=open]:bg-zinc-200/70"
				>
					<span className="flex-1 text-center">
						{ratio === "auto" ? "Auto" : ratio}
					</span>
					<span className="flex-1 border-l border-zinc-300/70 text-center">
						{resolution}
					</span>
					<ChevronDownIcon className="ml-1 h-4 w-4 shrink-0 text-zinc-500 lg:-rotate-90" />
				</button>
			}
		>
			<div className="space-y-4">
				<div className="space-y-2">
					<p className="text-xs font-medium text-zinc-700">
						Aspect Ratio
					</p>
					<div className="grid grid-cols-4 gap-2">
						{settings.aspectRatios.map((value) => (
							<button
								type="button"
								key={value}
								aria-pressed={ratio === value}
								className={optionClass(ratio === value)}
								onClick={() => setAspectRatio(value)}
							>
								<AspectRatioSwatch
									value={value}
									size={14}
									className={cn(
										"border bg-transparent",
										ratio === value
											? "border-white"
											: "border-zinc-500/70",
									)}
								/>
								{value === "auto" ? "Auto" : value}
							</button>
						))}
					</div>
				</div>
				<div className="space-y-2">
					<p className="text-xs font-medium text-zinc-700">
						Resolution
					</p>
					<div className="grid grid-cols-3 gap-2">
						{settings.resolutions.map((value) => (
							<button
								type="button"
								key={value}
								aria-pressed={resolution === value}
								className={cn(
									optionClass(resolution === value),
									lockedResolutions.has(value) &&
										"opacity-40 hover:opacity-60",
								)}
								onClick={() =>
									lockedResolutions.has(value)
										? onLockedResolution()
										: setResolution(value)
								}
							>
								{lockedResolutions.has(value) && (
									<LockIcon className="h-3 w-3" />
								)}
								{value}
							</button>
						))}
					</div>
				</div>
			</div>
		</GenerationSettings>
	);
}
